import { Component, inject, OnInit, viewChild } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import {
    AuthService, BreadcrumbService2, CommunityService, LotsService, ModalService, OrganizationService,
    OrgSelectionService, SubGroupsService, UserService
} from '../../../../services';
import { UnsubscribeOnDestroy } from '../../../../classes';
import { ApprovalHierarchy, ApprovalLimit, Claims, Lot, Market, Organization, SubGroup, User, UserFinancialCommunityAssoc } from '../../../../types';
import { CommunityViewModel } from './models/communityviewmodel';
import { forkJoin, of } from 'rxjs';
import { combineLatestWith, concatMap, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { ListboxModule } from 'primeng/listbox';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { SharedModule } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { CurrencyPipe } from '@angular/common';

@Component({
    selector: 'myuserinformation',
    templateUrl: './myuserinformation.component.html',
    styleUrls: ['./myuserinformation.component.scss'],
    standalone: true,
    imports: [FormsModule, TableModule, SharedModule, CheckboxModule, SelectModule, ListboxModule, ButtonModule, CurrencyPipe]
})
export class MyUserInformationComponent extends UnsubscribeOnDestroy implements OnInit {
    user: User = new User();
    selectedSubGroupID: number = 0;
    originalSubGroupID: number = 0;
    isAppWide: boolean = false;
    marketList: Organization[] = [];
    subGroupList: SubGroup[] = [];
    userCommunities: CommunityViewModel[] = [];
    marketCommunities: CommunityViewModel[] = [];
    communityOptions: CommunityViewModel[] = [];
    displayMarketCommunities: boolean = false;
    selectedUserFinancialCommunityAssoc: UserFinancialCommunityAssoc[] = [];
    assignedDefaultSelectedCommunities: CommunityViewModel[] = [];
    assignedDefaultMarketSelectedCommunities: CommunityViewModel[] = [];
    userDefaultSelectedCommunities: CommunityViewModel[] = [];
    selectedCommunities: CommunityViewModel[] = [];
    selectedSubGroupFilter: SubGroup;
    displayMySchedules: boolean = false;
    maxCommsAssigned: number = 10;

    readonly myUserInfoForm = viewChild<NgForm>("myuserinfoform");

    private authService = inject(AuthService);
    private breadcrumbService = inject(BreadcrumbService2);
    private modalService = inject(ModalService);
    private orgService = inject(OrganizationService);
    private userService = inject(UserService);
    private orgSelectionService = inject(OrgSelectionService);
    private lotsService = inject(LotsService);
    private subGroupsService = inject(SubGroupsService);
    private communityService = inject(CommunityService);

    constructor() { super(); }

    ngOnInit(): void {
        setTimeout(() => {
            let appFeature = this.breadcrumbService.lastAppFeature;
            this.breadcrumbService.setCrumbs({ icon: "fas fa-user", label: "My User Profile", title: "My User Profile", appKey: appFeature }, true);
        });

        this.loadData();
    }

    loadData(): void {
        this.orgSelectionService.selectedMarket
            .pipe(
                takeUntil(this.ngUnsubscribe$),
                combineLatestWith(this.userService.getMyUserProfile(), this.orgService.getActiveMarkets()),
                concatMap(([mkt, user, mkts]) => {
                    this.user = user;
                    this.isAppWide = this.authService.getClaimValue(Claims.orgAll);

                    //Use a separate variable for Markets so that when a User has no assigned Orgs (meaning they're technically assigned to them all)
                    //the application will display all markets but won't create new UserOrg records when saving to the database
                    this.marketList = this.user.userOrgs;

                    this.setMarketList(mkts);

                    this.setApprovalHierarchies();

                    this.setApprovalLimits();

                    return this.getCommunitiesForMySchedule$(mkt.orgId, this.user.azureGUID);
                }),
                map(() => this.marketList.map((m) => m.orgId)), //Return all market ids to retrieve subgroups
                switchMap(marketIds => {
                    let empty: SubGroup[] = [];
                    return !this.isAppWide ? this.subGroupsService.getSubGroups(marketIds) : of(empty);
                })
            )
            .subscribe(subgroups => {
                if (subgroups.length > 0) {
                    this.subGroupList = subgroups;
                    this.subGroupList.forEach((subGroup) => {
                        subGroup.divisionName = this.marketList.find((mkt) => mkt.orgId === subGroup.divisionID).orgName;
                    });
                }

                this.displayMarketCommunities = false;
                // My Schedules has not been completed and should not display - should also probably move to the User Settings Tab
                //this.displayMySchedules = this.user.role && !this.isAppWide && this.marketList.length === 1 ? true : false;
                this.displayMySchedules = false;
            });
    }

    getCommunitiesForMySchedule$(orgId: number, azureGUID: string) {
        return forkJoin([this.communityService.getConstructionActiveCommunities(orgId), this.lotsService.getLotsByUser(orgId, azureGUID)])
            .pipe(
                tap(([communities, lots]) => {
                    this.marketCommunities = communities as CommunityViewModel[];
                    this.marketCommunities.forEach(c => c.communityLabel = c.sourceKey !== null ? `${c.sourceKey} - ${c.orgName}` : c.orgName);

                    const tempCommunities: CommunityViewModel[] = lots.map((lot: Lot) => lot.community) as CommunityViewModel[];
                    this.userCommunities = tempCommunities.filter((v, i, a) => a.findIndex(t => (t.orgId === v.orgId)) === i);
                    this.userCommunities.forEach(c => c.communityLabel = c.sourceKey !== null ? `${c.sourceKey} - ${c.orgName}` : c.orgName);

                    this.assignedDefaultSelectedCommunities = this.userCommunities.slice(0, this.maxCommsAssigned);
                    this.assignedDefaultMarketSelectedCommunities = this.marketCommunities.filter((c: CommunityViewModel) => this.assignedDefaultSelectedCommunities.some((adsc: CommunityViewModel) => adsc.orgId === c.orgId));
                    this.selectedUserFinancialCommunityAssoc = this.user.userFinancialCommunityAssocs ? this.user.userFinancialCommunityAssocs.slice(0, this.maxCommsAssigned) : [];

                    const customMergeCommunities: CommunityViewModel[] = this.marketCommunities.filter((c: CommunityViewModel) => this.selectedUserFinancialCommunityAssoc.some((assoc: UserFinancialCommunityAssoc) => assoc.financialCommunityId === c.orgId));
                    this.userCommunities = this.userCommunities.concat(customMergeCommunities.filter((x: CommunityViewModel) => this.userCommunities.every((y: CommunityViewModel) => y.orgId !== x.orgId)));
                    this.userCommunities.sort((a, b) => (a.orgName > b.orgName) ? 1 : ((b.orgName > a.orgName) ? -1 : 0));

                    if (this.userCommunities.length > 0) {
                        this.selectedCommunities = this.selectedUserFinancialCommunityAssoc.length > 0 ? this.userCommunities.filter((c: CommunityViewModel) => this.selectedUserFinancialCommunityAssoc.some((assoc: UserFinancialCommunityAssoc) => assoc.financialCommunityId === c.orgId)) :
                            this.assignedDefaultSelectedCommunities;
                    } else {
                        this.selectedCommunities = this.marketCommunities.filter((c: CommunityViewModel) => this.selectedUserFinancialCommunityAssoc.some((ucp: UserFinancialCommunityAssoc) => ucp.financialCommunityId === c.orgId));
                    }

                    this.userDefaultSelectedCommunities = this.selectedCommunities;
                    this.communityOptions = this.userCommunities;
                }));
    }

    toggleMarketCommunities(allCommunities: boolean): void {
        this.communityOptions = allCommunities ? this.marketCommunities : this.userCommunities;
    }

    switchCommunityList(isMarketCommunity: boolean) {
        this.toggleMarketCommunities(isMarketCommunity);

        if (!isMarketCommunity && this.userCommunities.length > 0) {
            this.selectedCommunities = this.selectedUserFinancialCommunityAssoc.length > 0 ? this.userCommunities.filter((c: CommunityViewModel) => this.selectedUserFinancialCommunityAssoc.some((assoc: UserFinancialCommunityAssoc) => assoc.financialCommunityId === c.orgId)) :
                this.assignedDefaultSelectedCommunities;
        } else {
            this.selectedCommunities = this.selectedUserFinancialCommunityAssoc.length > 0 ? this.marketCommunities.filter((c: CommunityViewModel) => this.selectedUserFinancialCommunityAssoc.some((assoc: UserFinancialCommunityAssoc) => assoc.financialCommunityId === c.orgId)) :
                this.assignedDefaultMarketSelectedCommunities;
        }

        this.userDefaultSelectedCommunities = this.selectedCommunities;
    }

    onCommunitySelectionChange() {
        if (this.selectedCommunities.length > this.maxCommsAssigned) {
            this.selectedCommunities = this.selectedCommunities.slice(0, 10);
        }
    }

    onSubGroupFilter(subGroup: SubGroup): void {
        if (!subGroup) {
            this.toggleMarketCommunities(this.displayMarketCommunities);
            return;
        }

        this.communityOptions = this.marketCommunities.filter(x => subGroup.communities.some(sc => sc.orgId === x.orgId));
    }

    revertCommunitySelectionAssignment() {
        if (!this.displayMarketCommunities) {
            this.selectedCommunities = this.assignedDefaultSelectedCommunities;
        } else {
            this.selectedCommunities = this.assignedDefaultMarketSelectedCommunities;
        }
    }

    areCommunityArraysEqual() {
        let isSame: boolean = false;

        if (this.userDefaultSelectedCommunities.length === this.selectedCommunities.length) {
            isSame = this.userDefaultSelectedCommunities.every((c: CommunityViewModel) => {
                return this.selectedCommunities.find((t: CommunityViewModel) => t.orgId === c.orgId) !== undefined;
            });
        }

        return isSame;
    }

    private setMarketList(allActiveMarkets: Market[]): void {
        //Assign Org Names for Market List since they're not stored in PCP DB
        this.marketList.forEach((org: Organization) => {
            const market = allActiveMarkets.find((mkt) => mkt.orgId === org.orgId);
            org.orgName = market.orgName;
        });

        //If User has no Org assignments in PCP DB then they are technially assigned to ALL Orgs
        if (this.marketList.length === 0) {
            this.marketList = allActiveMarkets;
            this.isAppWide = true;
        }

        //Sort Markets in alphabetical order
        this.marketList = this.marketList.sort((a, b) => a.orgName.localeCompare(b.orgName));
    }

    private setApprovalHierarchies(): void {
        //Assign Org Names for Approval Hierarchy Divisions since they're not stored in PCP DB (Display only Managers for the Markets the user is currently assigned to)
        this.user.approvalHierarchies.forEach((approvalHierarchy: ApprovalHierarchy) => {
            if (this.marketList.some((m) => m.orgId === approvalHierarchy.marketId)) {
                approvalHierarchy.marketName = this.marketList.find((m) => m.orgId === approvalHierarchy.marketId).orgName;
            } else {
                approvalHierarchy.marketName = null;
            }
        });

        this.user.approvalHierarchies = this.user.approvalHierarchies.filter((approvalHierarchy: ApprovalHierarchy) => approvalHierarchy.marketName !== null);
        this.user.approvalHierarchies.sort((a, b) => (a.marketName > b.marketName) ? 1 : ((b.marketName > a.marketName) ? -1 : 0));
    }

    private setApprovalLimits(): void {
        //Assign Org Names for Approval Limit Divisions since they're not stored in PCP DB (Display only Approval Limits for the Markets the user is currently assigned to)
        const approvalLimits: ApprovalLimit[] = [];

        this.user.approvalLimits.forEach((limit: ApprovalLimit) => {
            const market = this.marketList.find((mkt) => mkt.orgId === limit.marketId);
            if (market) {
                limit.marketName = market.orgName;
                approvalLimits.push(limit);
            }
        });

        this.user.approvalLimits = approvalLimits;
        this.user.approvalLimits.sort((a, b) => (a.marketName > b.marketName) ? 1 : ((b.marketName > a.marketName) ? -1 : 0));
    }
}
